// ==================== إحصائيات إرسال الإيرادات للمدير ====================
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import useElementToPdf from '../../utils/useElementToPdf';
import { 
  Clock, 
  Send, 
  CheckCircle2, 
  AlertCircle,
  Printer,
  Search,
  BarChart3,
  Users,
  TrendingUp,
  RotateCcw,
  Hash,
  Settings,
  FileText,
  RotateCw
} from 'lucide-react';
import api from '../../utils/api';

const RevenueStats = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchParams, setSearchParams] = useState({
    receipt_number: '',
    barber_id: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: ''
  });
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [barbers, setBarbers] = useState([]);
  // عدادات الإيصالات
  const [receiptCounters, setReceiptCounters] = useState(null);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [resettingCounter, setResettingCounter] = useState(null);
  // إعدادات الطباعة
  const [printSettings, setPrintSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  // تنسيقات الإيصالات
  const [receiptFormats, setReceiptFormats] = useState([]);
  const [pendingFormats, setPendingFormats] = useState([]);
  const [editingFormat, setEditingFormat] = useState(null);
  const [newFormat, setNewFormat] = useState('');
  
  // ref for printing receipts
  const receiptPrintRef = useRef();
  
  // react-to-print hook for receipt
  const handleReceiptPrintAction = useElementToPdf({
    contentRef: receiptPrintRef,
    documentTitle: 'إيصال_طباعة',
    pageStyle: `
      @page { size: 80mm auto; margin: 5mm; }
      @media print {
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; direction: rtl; }
      }
    `
  });

  useEffect(() => {
    fetchStats();
    fetchBarbers();
    fetchReceiptCounters();
    fetchPrintSettings();
    fetchReceiptFormats();
    fetchPendingFormats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/revenues/pending-stats');
      setStats(res.data);
    } catch (error) {
      toast.error('خطأ في جلب الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptCounters = async () => {
    try {
      setLoadingCounters(true);
      const res = await api.get('/revenues/receipt-counters');
      setReceiptCounters(res.data);
    } catch (error) {
      console.error('Error fetching receipt counters:', error);
    } finally {
      setLoadingCounters(false);
    }
  };

  const handleResetCounter = async (barberId, barberName) => {
    if (!window.confirm(`هل تريد تصفير عداد إيصالات ${barberName}؟`)) return;
    
    setResettingCounter(barberId);
    try {
      await api.post(`/revenues/reset-receipt-counter/${barberId}`);
      toast.success(`تم تصفير عداد ${barberName}`);
      fetchReceiptCounters();
    } catch (error) {
      toast.error('خطأ في تصفير العداد');
    } finally {
      setResettingCounter(null);
    }
  };

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers/all');
      setBarbers(res.data || []);
    } catch (error) {
      console.error('Error fetching barbers:', error);
    }
  };

  const fetchPrintSettings = async () => {
    try {
      const res = await api.get('/revenues/print-settings');
      setPrintSettings(res.data);
    } catch (error) {
      console.error('Error fetching print settings:', error);
    }
  };

  const fetchReceiptFormats = async () => {
    try {
      const res = await api.get('/revenues/receipt-formats/all');
      setReceiptFormats(res.data || []);
    } catch (error) {
      console.error('Error fetching receipt formats:', error);
    }
  };

  const fetchPendingFormats = async () => {
    try {
      const res = await api.get('/revenues/receipt-formats/pending');
      setPendingFormats(res.data || []);
    } catch (error) {
      console.error('Error fetching pending formats:', error);
    }
  };

  const handleUpdatePrintSettings = async (key, value) => {
    setSavingSettings(true);
    try {
      await api.put('/revenues/print-settings', { [key]: value });
      toast.success('تم حفظ الإعدادات');
      fetchPrintSettings();
    } catch (error) {
      toast.error('خطأ في حفظ الإعدادات');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSetFormat = async (barberId) => {
    if (!newFormat.trim()) {
      toast.error('يرجى إدخال تنسيق');
      return;
    }
    try {
      await api.put(`/revenues/receipt-format/${barberId}`, { format_template: newFormat });
      toast.success('تم تعيين التنسيق');
      setEditingFormat(null);
      setNewFormat('');
      fetchReceiptFormats();
    } catch (error) {
      toast.error('خطأ في تعيين التنسيق');
    }
  };

  const handleApproveFormat = async (barberId) => {
    try {
      await api.put(`/revenues/receipt-format/${barberId}/approve`);
      toast.success('تم اعتماد التنسيق');
      fetchPendingFormats();
      fetchReceiptFormats();
    } catch (error) {
      toast.error('خطأ في اعتماد التنسيق');
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.receipt_number) params.append('receipt_number', searchParams.receipt_number);
      if (searchParams.barber_id) params.append('barber_id', searchParams.barber_id);
      if (searchParams.start_date) params.append('start_date', searchParams.start_date);
      if (searchParams.end_date) params.append('end_date', searchParams.end_date);
      if (searchParams.min_amount) params.append('min_amount', searchParams.min_amount);
      if (searchParams.max_amount) params.append('max_amount', searchParams.max_amount);
      
      const res = await api.get(`/revenues/search-receipts?${params.toString()}`);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('خطأ في البحث');
    } finally {
      setSearching(false);
    }
  };

  // State for current receipt to print
  const [currentReceipt, setCurrentReceipt] = useState(null);

  const handlePrintReceipt = async (revenueId) => {
    try {
      const res = await api.post(`/revenues/${revenueId}/print-receipt`);
      const receipt = res.data.receipt;
      setCurrentReceipt(receipt);
      // Use react-to-print after setting receipt data
      setTimeout(() => {
        handleReceiptPrintAction();
        toast.success('تم فتح نافذة الطباعة');
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل طباعة الإيصال');
    }
  };

  const formatHours = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} دقيقة`;
    if (hours < 24) return `${hours.toFixed(1)} ساعة`;
    return `${(hours / 24).toFixed(1)} يوم`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0d0d0d] to-[#1a1a1a] p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#d4af37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d0d0d] to-[#1a1a1a] p-4" dir="rtl">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* العنوان */}
        <div>
          <h1 className="text-2xl font-bold text-[#d4af37] mb-2">إحصائيات الإيرادات</h1>
          <p className="text-gray-400 text-sm">متابعة حالة إرسال الإيرادات ونسب الطباعة وإعدادات الطباعة</p>
        </div>

        {/* إعدادات الطباعة */}
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Settings className="w-5 h-5 text-[#d4af37]" />
              إعدادات الطباعة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* حجم الإيصال */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">حجم إيصال الإيراد الواحد</label>
                <div className="grid grid-cols-2 gap-2">
                  {printSettings?.available_receipt_sizes && Object.entries(printSettings.available_receipt_sizes).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleUpdatePrintSettings('receipt_size', key)}
                      disabled={savingSettings}
                      className={`p-3 rounded-lg border text-sm transition-all ${
                        printSettings?.receipt_size === key
                          ? 'border-[#d4af37] bg-[#d4af37]/20 text-[#d4af37]'
                          : 'border-[#444] bg-[#252525] text-gray-300 hover:border-[#d4af37]/50'
                      }`}
                      data-testid={`receipt-size-${key}`}
                    >
                      <div className="font-semibold">{value.name}</div>
                      <div className="text-xs text-gray-500">{value.width} × {value.height}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* اتجاه صفحة التقارير */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">اتجاه صفحة التقارير</label>
                <div className="flex gap-2">
                  {printSettings?.available_orientations && Object.entries(printSettings.available_orientations).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleUpdatePrintSettings('report_orientation', key)}
                      disabled={savingSettings}
                      className={`flex-1 p-4 rounded-lg border text-sm transition-all flex items-center justify-center gap-2 ${
                        printSettings?.report_orientation === key
                          ? 'border-[#d4af37] bg-[#d4af37]/20 text-[#d4af37]'
                          : 'border-[#444] bg-[#252525] text-gray-300 hover:border-[#d4af37]/50'
                      }`}
                      data-testid={`orientation-${key}`}
                    >
                      {key === 'portrait' ? (
                        <FileText className="w-6 h-6" />
                      ) : (
                        <RotateCw className="w-6 h-6" />
                      )}
                      <span>{value.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* تنسيقات أرقام الإيصالات */}
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Hash className="w-5 h-5 text-[#d4af37]" />
              تنسيقات أرقام الإيصالات
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* المتغيرات المتاحة */}
            <div className="mb-4 p-3 bg-[#252525] rounded-lg">
              <p className="text-sm text-gray-400 mb-2">المتغيرات المتاحة:</p>
              <div className="flex flex-wrap gap-2">
                {['{salon}', '{barber}', '{barber_full}', '{year}', '{year_short}', '{month}', '{day}', '{number}', '{number_4}', '{number_5}'].map(v => (
                  <code key={v} className="px-2 py-1 bg-[#333] text-[#d4af37] rounded text-xs">{v}</code>
                ))}
              </div>
            </div>

            {/* التنسيقات المنتظرة */}
            {pendingFormats.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-500 font-semibold mb-2">تنسيقات تنتظر الاعتماد:</p>
                {pendingFormats.map(f => (
                  <div key={f.barber_id} className="flex items-center justify-between py-2">
                    <div>
                      <span className="text-white">{f.barber_name}:</span>
                      <code className="mx-2 px-2 py-1 bg-[#333] text-[#d4af37] rounded text-sm">{f.proposed_format}</code>
                    </div>
                    <Button
                      onClick={() => handleApproveFormat(f.barber_id)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 ml-1" />
                      اعتماد
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* جدول التنسيقات */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#252525]">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-300">الحلاق</th>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-300">التنسيق</th>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-300">مثال</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptFormats.map((f) => (
                    <tr key={f.barber_id} className="border-b border-[#333] hover:bg-[#252525]">
                      <td className="px-4 py-3 font-semibold text-white">{f.barber_name}</td>
                      <td className="px-4 py-3">
                        {editingFormat === f.barber_id ? (
                          <Input
                            value={newFormat}
                            onChange={(e) => setNewFormat(e.target.value)}
                            className="bg-[#333] border-[#444] text-sm"
                            placeholder="مثال: KE-{barber}-{year}-{number_4}"
                          />
                        ) : (
                          <code className="px-2 py-1 bg-[#333] text-[#d4af37] rounded text-sm">{f.format}</code>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-sm">{f.example}</td>
                      <td className="px-4 py-3 text-center">
                        {editingFormat === f.barber_id ? (
                          <div className="flex gap-2 justify-center">
                            <Button size="sm" onClick={() => handleSetFormat(f.barber_id)} className="bg-green-600">
                              حفظ
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setEditingFormat(null); setNewFormat(''); }}>
                              إلغاء
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#d4af37] text-[#d4af37]"
                            onClick={() => { setEditingFormat(f.barber_id); setNewFormat(f.format); }}
                          >
                            تعديل
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* الإحصائيات العامة */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">إيرادات منتظرة</p>
                  <p className="text-3xl font-bold text-yellow-500">{stats?.total_pending || 0}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">إيرادات مُرسلة</p>
                  <p className="text-3xl font-bold text-green-500">{stats?.total_sent || 0}</p>
                </div>
                <CheckCircle2 className="w-10 h-10 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">إجمالي الإيرادات</p>
                  <p className="text-3xl font-bold text-[#d4af37]">{(stats?.total_pending || 0) + (stats?.total_sent || 0)}</p>
                </div>
                <BarChart3 className="w-10 h-10 text-[#d4af37]/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* إحصائيات كل حلاق */}
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Users className="w-5 h-5 text-[#d4af37]" />
              إحصائيات الحلاقين
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#252525]">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-300">الحلاق</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">منتظرة</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">مُرسلة</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">تلقائية</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">متوسط الإرسال</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">نسبة الطباعة</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.barbers_stats?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                        لا توجد بيانات
                      </td>
                    </tr>
                  ) : (
                    stats?.barbers_stats?.map((barber) => (
                      <tr key={barber.barber_id} className="border-b border-[#333] hover:bg-[#252525]">
                        <td className="px-4 py-3 font-semibold text-white">{barber.barber_name}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-sm ${barber.pending_count > 0 ? 'bg-yellow-500/20 text-yellow-500' : 'bg-gray-500/20 text-gray-400'}`}>
                            {barber.pending_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 rounded-full text-sm bg-green-500/20 text-green-500">
                            {barber.sent_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 rounded-full text-sm bg-blue-500/20 text-blue-500">
                            {barber.auto_sent_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="flex items-center justify-center gap-1 text-sm text-gray-300">
                            <Clock className="w-4 h-4" />
                            {formatHours(barber.avg_send_time_hours)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-[#d4af37] h-2 rounded-full" 
                                style={{ width: `${Math.min(barber.print_percentage, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-300">{barber.print_percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* عدادات أرقام الإيصالات */}
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Hash className="w-5 h-5 text-[#d4af37]" />
              عدادات أرقام الإيصالات
              <span className="text-sm font-normal text-gray-400 mr-2">
                (سنة {receiptCounters?.year || new Date().getFullYear()})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCounters ? (
              <div className="text-center py-8 text-gray-500">جاري التحميل...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#252525]">
                    <tr>
                      <th className="px-4 py-3 text-start text-sm font-semibold text-gray-300">الحلاق</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">آخر رقم إيصال</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">تنسيق الرقم</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {barbers.filter(b => !b.is_excluded).map((barber) => {
                      const counter = receiptCounters?.counters?.find(c => c.barber_id === barber.id);
                      const currentCount = counter?.counter || 0;
                      const barberShort = barber.id.replace('barber-', '').substring(0, 3).toUpperCase();
                      const year = receiptCounters?.year || new Date().getFullYear();
                      
                      return (
                        <tr key={barber.id} className="border-b border-[#333] hover:bg-[#252525]">
                          <td className="px-4 py-3 font-semibold text-white">{barber.name}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-3 py-1 rounded-full text-sm bg-[#d4af37]/20 text-[#d4af37] font-mono">
                              {currentCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 font-mono text-sm">
                            KE-{barberShort}-{year}-{String(currentCount + 1).padStart(4, '0')}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              onClick={() => handleResetCounter(barber.id, barber.name)}
                              disabled={resettingCounter === barber.id || currentCount === 0}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                              data-testid={`reset-counter-${barber.id}`}
                            >
                              <RotateCcw className={`w-4 h-4 ml-1 ${resettingCounter === barber.id ? 'animate-spin' : ''}`} />
                              تصفير
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4">
              * يُصفَّر العداد تلقائياً في بداية كل سنة جديدة
            </p>
          </CardContent>
        </Card>

        {/* البحث في الإيصالات */}
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Search className="w-5 h-5 text-[#d4af37]" />
              البحث في الإيصالات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">رقم الإيصال</label>
                <Input
                  placeholder="KE-20260320-0001"
                  value={searchParams.receipt_number}
                  onChange={(e) => setSearchParams({...searchParams, receipt_number: e.target.value})}
                  className="bg-[#252525] border-[#444]"
                  data-testid="search-receipt-number"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">الحلاق</label>
                <select
                  value={searchParams.barber_id}
                  onChange={(e) => setSearchParams({...searchParams, barber_id: e.target.value})}
                  className="w-full bg-[#252525] border border-[#444] rounded-md p-2 text-white"
                  data-testid="search-barber"
                >
                  <option value="">الكل</option>
                  {barbers.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">من تاريخ</label>
                <Input
                  type="date"
                  value={searchParams.start_date}
                  onChange={(e) => setSearchParams({...searchParams, start_date: e.target.value})}
                  className="bg-[#252525] border-[#444]"
                  data-testid="search-start-date"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">إلى تاريخ</label>
                <Input
                  type="date"
                  value={searchParams.end_date}
                  onChange={(e) => setSearchParams({...searchParams, end_date: e.target.value})}
                  className="bg-[#252525] border-[#444]"
                  data-testid="search-end-date"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">الحد الأدنى للمبلغ</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={searchParams.min_amount}
                  onChange={(e) => setSearchParams({...searchParams, min_amount: e.target.value})}
                  className="bg-[#252525] border-[#444]"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">الحد الأقصى للمبلغ</label>
                <Input
                  type="number"
                  placeholder="1000"
                  value={searchParams.max_amount}
                  onChange={(e) => setSearchParams({...searchParams, max_amount: e.target.value})}
                  className="bg-[#252525] border-[#444]"
                />
              </div>
            </div>
            <Button 
              onClick={handleSearch}
              disabled={searching}
              className="bg-[#d4af37] hover:bg-[#c9a030] text-black"
              data-testid="search-receipts-btn"
            >
              <Search className="w-4 h-4 ml-2" />
              {searching ? 'جاري البحث...' : 'بحث'}
            </Button>

            {/* نتائج البحث */}
            {searchResults && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  نتائج البحث ({searchResults.count} إيصال)
                </h3>
                {searchResults.count === 0 ? (
                  <p className="text-gray-500 text-center py-4">لا توجد نتائج</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#252525]">
                        <tr>
                          <th className="px-4 py-2 text-start text-sm">رقم الإيصال</th>
                          <th className="px-4 py-2 text-start text-sm">الحلاق</th>
                          <th className="px-4 py-2 text-start text-sm">التاريخ</th>
                          <th className="px-4 py-2 text-start text-sm">المبلغ</th>
                          <th className="px-4 py-2 text-start text-sm">الطباعات</th>
                          <th className="px-4 py-2 text-start text-sm">إجراء</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.receipts.map((receipt) => (
                          <tr key={receipt.id} className="border-b border-[#333]">
                            <td className="px-4 py-2 font-mono text-[#d4af37]">{receipt.receipt_number}</td>
                            <td className="px-4 py-2">{receipt.barber_name}</td>
                            <td className="px-4 py-2">{receipt.date?.substring(0, 10)}</td>
                            <td className="px-4 py-2 font-semibold">{receipt.amount} د.إ</td>
                            <td className="px-4 py-2">{receipt.print_count || 0}</td>
                            <td className="px-4 py-2">
                              <Button
                                onClick={() => handlePrintReceipt(receipt.id)}
                                size="sm"
                                variant="outline"
                                className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10"
                              >
                                <Printer className="w-4 h-4 ml-1" />
                                تنزيل PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Hidden printable receipt component */}
      {currentReceipt && (
        <div style={{ display: 'none' }}>
          <div ref={receiptPrintRef} style={{ 
            fontFamily: 'Arial, sans-serif', 
            padding: '20px', 
            maxWidth: '400px', 
            margin: 'auto',
            direction: 'rtl',
            background: 'white',
            color: 'black'
          }}>
            <div style={{ textAlign: 'center', borderBottom: '2px dashed #ccc', paddingBottom: '10px', marginBottom: '10px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d4af37' }}>{currentReceipt.salon_name}</div>
              <div>إيصال دفع</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span>رقم الإيصال:</span>
              <strong>{currentReceipt.receipt_number}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span>التاريخ:</span>
              <span>{currentReceipt.date}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span>الوقت:</span>
              <span>{currentReceipt.time}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span>الحلاق:</span>
              <span>{currentReceipt.barber_name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span>الخدمة:</span>
              <span>{currentReceipt.service_name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: 'bold', borderTop: '2px dashed #ccc', paddingTop: '10px', marginTop: '10px' }}>
              <span>المبلغ:</span>
              <span style={{ color: '#d4af37' }}>{currentReceipt.amount} د.إ</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#666' }}>
              <p>شكراً لزيارتكم</p>
              <p>نتمنى لكم يوماً سعيداً</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueStats;
